/**
 * Widget V2 — Services menu (discovery)
 *
 * The full catalogue of bookable services, filtered with the exact same
 * controls as the booking views: FilterChip + bottom sheet on mobile,
 * FilterDropdown selects on desktop. Categories are horizontal pill tabs right
 * above the list they narrow. Rows carry a photo-or-icon thumb, an
 * always-present facts line (coaches · durations · locations) with the club
 * description below it, and a direct Book shortcut so the detail page is
 * optional. Data is entirely from the init payload (`services`).
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState, FilterDropdown } from "/imports/ui/components/shared";
import { getServiceDurationOptions, FilterChip, OptionRow } from "/imports/ui/components/booking";
import { PageHeading, ServiceThumb, bookingHref, CATEGORY_ROUTE, SECTION_LABEL_SX, DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";

// Categories that represent bookable "services" worth listing in the menu.
const MENU_CATEGORY_TYPES = ["category-service-private", "category-service-court", "category-service-class", "category-service-term"];

const SHEET_TITLES = { sport: "Sport", location: "Location", level: "Level" };

function CategoryPill({ label, active, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: 1.75,
        py: 0.65,
        borderRadius: 999,
        fontSize: "0.8rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        flexShrink: 0,
        border: "1px solid",
        borderColor: active ? "primary.main" : "divider",
        bgcolor: active ? "primary.main" : "background.paper",
        color: active ? "common.white" : "text.primary",
        transition: "all 0.15s ease"
      }}
    >
      {label}
    </ButtonBase>
  );
}

function ServiceRow({ service, sportName, locationCount, currency, onOpen, onBook }) {
  const levels = service.levelRequirements || [];
  // "From" price + offered lengths, straight from the init payload's pricing —
  // price is the single most useful fact on a service row.
  const durationOptions = getServiceDurationOptions(service);
  const prices = durationOptions.map((o) => o.price).filter((p) => p != null);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const isFrom = prices.length > 1 || durationOptions.some((o) => o.isFrom);
  const durationsLabel = durationOptions.length ? durationOptions.map((o) => o.value).join(" / ") + " min" : null;

  // The facts line every row leads with — description (when written) reads as
  // extra color below it, and the price cell doesn't need to repeat durations.
  const coachCount = (service.trainers || []).length;
  const metaLine = [
    coachCount > 0 ? `${coachCount} coach${coachCount > 1 ? "es" : ""}` : null,
    durationsLabel,
    locationCount > 1 ? `${locationCount} locations` : null
  ]
    .filter(Boolean)
    .join(" · ");

  const canBook = Boolean(CATEGORY_ROUTE[service.categoryType]);

  return (
    <Box
      onClick={onOpen}
      sx={{
        ...DISCOVERY_CARD_SX,
        p: { xs: 1.5, md: 2 },
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.25, md: 1.5 },
        cursor: "pointer",
        transition: "border-color 0.15s ease",
        "&:hover": { borderColor: "primary.main" }
      }}
    >
      <ServiceThumb service={service} size={{ xs: 44, md: 56 }} iconSize={{ xs: 20, md: 24 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography fontWeight={600} sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}>
            {service.name}
          </Typography>
          {sportName && (
            <Chip
              size="small"
              label={sportName}
              sx={{
                height: { xs: "1.125rem", md: "1.25rem" },
                fontSize: { xs: "0.62rem", md: "0.68rem" },
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                color: "primary.main",
                fontWeight: 600
              }}
            />
          )}
          {levels.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`Levels ${levels.join(", ")}`}
              sx={{ height: { xs: "1.125rem", md: "1.25rem" }, fontSize: { xs: "0.62rem", md: "0.68rem" } }}
            />
          )}
        </Box>
        {metaLine && (
          <Typography sx={{ mt: 0.25, fontSize: "0.72rem", fontWeight: 500, color: "text.secondary" }} noWrap>
            {metaLine}
          </Typography>
        )}
        {service.description && (
          <Typography
            color="text.secondary"
            sx={{
              mt: 0.25,
              fontSize: { xs: "0.78rem", md: "0.875rem" },
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}
          >
            {service.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        {minPrice != null && (
          <Typography fontWeight={700} sx={{ lineHeight: 1.2, whiteSpace: "nowrap", fontSize: { xs: "0.9rem", md: "1rem" } }}>
            {isFrom ? "from " : ""}
            {currency} {minPrice}
          </Typography>
        )}
        {canBook && (
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onBook();
            }}
            sx={{
              display: "block",
              mt: 0.75,
              ml: "auto",
              minWidth: 0,
              px: 1.75,
              py: 0.4,
              fontSize: "0.75rem",
              textTransform: "none",
              fontWeight: 600,
              color: "common.white"
            }}
          >
            Book
          </Button>
        )}
      </Box>
      <ChevronRightIcon sx={{ color: "text.secondary", flexShrink: 0, display: { xs: "none", md: "block" } }} />
    </Box>
  );
}

function ServicesView() {
  const navigate = useNavigate();
  const { slug, services, sports, locations, currency } = useWidget();

  const menuServices = useMemo(() => (services || []).filter((s) => MENU_CATEGORY_TYPES.includes(s.categoryType)), [services]);

  const sportName = (id) => (sports || []).find((s) => s._id === id)?.name;

  // null = no narrowing ("All"), matching the booking views' filter semantics.
  const [sportFilter, setSportFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [levelFilter, setLevelFilter] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null); // 'sport' | 'location' | 'level' | null
  const closeSheet = () => setActiveSheet(null);

  // Filter option lists, derived from the menu services so we only ever offer
  // values that can actually match something.
  const availableSports = useMemo(() => {
    const ids = new Set(menuServices.map((s) => s.sportId).filter(Boolean));
    return (sports || []).filter((s) => ids.has(s._id));
  }, [menuServices, sports]);

  const availableCategories = useMemo(() => {
    const seen = new Map();
    for (const s of menuServices) {
      const key = s.categoryName || s.categoryType;
      if (!seen.has(key)) seen.set(key, key);
    }
    return Array.from(seen.keys());
  }, [menuServices]);

  const availableLocations = useMemo(() => {
    const ids = new Set(menuServices.flatMap((s) => s.locationIds || []));
    return (locations || []).filter((l) => ids.has(l._id));
  }, [menuServices, locations]);

  const availableLevels = useMemo(() => {
    const set = new Set(menuServices.flatMap((s) => s.levelRequirements || []));
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [menuServices]);

  const visible = useMemo(
    () =>
      menuServices.filter((s) => {
        if (sportFilter && s.sportId !== sportFilter) return false;
        if (categoryFilter && (s.categoryName || s.categoryType) !== categoryFilter) return false;
        if (locationFilter && !(s.locationIds || []).includes(locationFilter)) return false;
        if (levelFilter != null && !(s.levelRequirements || []).map(String).includes(String(levelFilter))) return false;
        return true;
      }),
    [menuServices, sportFilter, categoryFilter, locationFilter, levelFilter]
  );

  // Group by category name, preserving first-seen order.
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of visible) {
      const key = s.categoryName || s.categoryType;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return Array.from(map.entries());
  }, [visible]);

  // Same controls as the booking views: chips + bottom sheet on mobile,
  // dropdown selects on desktop. Only filters that can narrow anything render.
  const filterDefs = [
    availableSports.length > 1 && {
      key: "sport",
      label: "Sport",
      value: sportFilter,
      valueLabel: sportName(sportFilter) || "All",
      options: availableSports.map((s) => ({ value: s._id, label: s.name })),
      allLabel: "All sports",
      set: setSportFilter
    },
    availableLocations.length > 1 && {
      key: "location",
      label: "Location",
      value: locationFilter,
      valueLabel: availableLocations.find((l) => l._id === locationFilter)?.name || "All",
      options: availableLocations.map((l) => ({ value: l._id, label: l.name })),
      allLabel: "All locations",
      set: setLocationFilter
    },
    availableLevels.length > 0 && {
      key: "level",
      label: "Level",
      value: levelFilter,
      valueLabel: levelFilter != null ? `Level ${levelFilter}` : "All",
      options: availableLevels.map((lvl) => ({ value: lvl, label: `Level ${lvl}` })),
      allLabel: "All levels",
      set: setLevelFilter
    }
  ].filter(Boolean);

  const activeDef = filterDefs.find((d) => d.key === activeSheet);
  const anyActive = filterDefs.some((d) => d.value != null);
  const clearFilters = () => filterDefs.forEach((d) => d.set(null));

  return (
    <Box>
      <PageHeading title="Services" subtitle="Everything you can book here, with the details that matter." onBack={() => navigate(`/${slug}`)} />

      {filterDefs.length > 0 && (
        <>
          {/* Mobile: FilterChip grid opening bottom sheets (booking pattern).
              An odd last chip spans the full row so the grid stays clean. */}
          <Box sx={{ display: { xs: "grid", md: "none" }, gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
            {filterDefs.map((d, i) => (
              <Box key={d.key} sx={{ gridColumn: filterDefs.length % 2 === 1 && i === filterDefs.length - 1 ? "1 / -1" : "auto" }}>
                <FilterChip label={d.label} value={d.valueLabel} active={d.value != null} onClick={() => setActiveSheet(d.key)} />
              </Box>
            ))}
          </Box>

          {/* Desktop: dropdown selects, as in the booking sidebars. */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
            {filterDefs.map((d) => (
              <FilterDropdown
                key={d.key}
                label={d.label}
                options={d.options}
                value={d.value}
                onChange={d.set}
                allLabel={d.allLabel}
                active={d.value != null}
              />
            ))}
            {anyActive && (
              <Button size="small" onClick={clearFilters} sx={{ textTransform: "none", fontWeight: 600 }}>
                Clear
              </Button>
            )}
          </Box>
        </>
      )}

      {availableCategories.length > 1 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 0.5,
            mb: 3,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}
        >
          <CategoryPill label="All" active={!categoryFilter} onClick={() => setCategoryFilter(null)} />
          {availableCategories.map((c) => (
            <CategoryPill key={c} label={c} active={categoryFilter === c} onClick={() => setCategoryFilter(c)} />
          ))}
        </Box>
      )}

      {groups.length === 0 ? (
        <EmptyState title="No services listed" description="There are no services to show for this selection." />
      ) : (
        groups.map(([categoryName, items]) => (
          <Box key={categoryName} sx={{ mb: { xs: 3, md: 4 } }}>
            {/* A lone group header repeats the active pill (or the only
                category) — show headers only when they separate something. */}
            {groups.length > 1 && <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>{categoryName}</Typography>}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {items.map((s) => (
                <ServiceRow
                  key={s._id}
                  service={s}
                  sportName={sportName(s.sportId)}
                  locationCount={(s.locationIds || []).length}
                  currency={currency}
                  onOpen={() => navigate(`/${slug}/services/${s._id}`)}
                  onBook={() => navigate(bookingHref(slug, s))}
                />
              ))}
            </Box>
          </Box>
        ))
      )}

      {/* Mobile filter bottom sheet — same Drawer chrome as the booking views. */}
      <Drawer
        anchor="bottom"
        open={!!activeDef}
        onClose={closeSheet}
        PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "75vh", px: 2.5, pt: 2, pb: 3 } }}
      >
        <Box sx={{ width: "2.5rem", height: "0.25rem", borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }}>{SHEET_TITLES[activeSheet] || ""}</Typography>
          <IconButton size="small" onClick={closeSheet} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>
          {activeDef && (
            <>
              <OptionRow
                label={activeDef.allLabel}
                selected={activeDef.value == null}
                onClick={() => {
                  activeDef.set(null);
                  closeSheet();
                }}
              />
              {activeDef.options.map((opt) => (
                <OptionRow
                  key={opt.value}
                  label={opt.label}
                  selected={activeDef.value === opt.value}
                  onClick={() => {
                    activeDef.set(opt.value);
                    closeSheet();
                  }}
                />
              ))}
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

export default ServicesView;
